import { styled } from '@openeducation/oe-it-themes';
import React from 'react';
import { StyleProp, useWindowDimensions, View, ViewStyle } from 'react-native';

import { PopupPosition } from './popup.types';
import { ExtendedLayoutRectangle } from './use-popup';

type PopupInnerContainerProps = {
  finalPopupPosition: PopupPosition;
  childrenRect?: ExtendedLayoutRectangle;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

export const PopupInnerContainer = ({
  finalPopupPosition,
  childrenRect,
  children,
  style,
}: PopupInnerContainerProps) => {
  const { width } = useWindowDimensions();
  const containerRef = React.useRef<View>(null);
  const [popupRect, setPopupRect] = React.useState<ExtendedLayoutRectangle>();

  const { leftSpacing, rightSpacing } = React.useMemo(() => {
    if (!childrenRect || !popupRect) {
      return { leftSpacing: 0, rightSpacing: 0 };
    }

    if (finalPopupPosition.includes('left')) {
      const leftSpacing = childrenRect.x - Math.abs(popupRect.x);
      return { leftSpacing: leftSpacing > 0 ? 0 : leftSpacing };
    } else if (finalPopupPosition.includes('right')) {
      const rightSpacing = width - (childrenRect.x + popupRect.width);
      return { rightSpacing: rightSpacing > 0 ? 0 : rightSpacing };
    } else {
      const center = childrenRect.pageX + childrenRect.width / 2;
      if (popupRect.width / 2 > center) {
        return { leftSpacing: center - popupRect.width };
      } else if (popupRect.width / 2 > width - center) {
        return { rightSpacing: width - center - popupRect.width };
      }
    }
    return { leftSpacing: 0, rightSpacing: 0 };
  }, [finalPopupPosition, childrenRect, popupRect, width]);

  React.useEffect(() => {
    setTimeout(
      () =>
        containerRef.current?.measure((x, y, width, height, pageX, pageY) => {
          setPopupRect({ x, y, width, height, pageX, pageY });
        }),
      0
    );
  }, []);

  return (
    <Container
      ref={containerRef}
      collapsable={false}
      popupPosition={finalPopupPosition}
      leftSpacing={leftSpacing}
      rightSpacing={rightSpacing}
      style={style}
    >
      {children}
    </Container>
  );
};

interface ContainerProps {
  popupPosition: PopupPosition;
  leftSpacing?: number;
  rightSpacing?: number;
}

const Container = styled.View<ContainerProps>`
  margin-right: ${({ popupPosition, leftSpacing = 0 }) =>
    popupPosition.includes('left') ? -8 + leftSpacing : leftSpacing}px;
  margin-left: ${({ popupPosition, rightSpacing = 0 }) =>
    popupPosition.includes('right') ? -8 + rightSpacing : rightSpacing}px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme?.color?.background ?? 'white'};
  border: 1px solid ${({ theme }) => theme?.color?.border ?? 'white'};
`;
